// Shared surface states. Every Brain panel renders exactly one of these until it
// has real data. A "disabled" (backend 404) state is deliberately calm and
// informational — NOT an error.
import React from "react";
import { Skeleton } from "../ui/skeleton";

export function LoadingState({ label = "Loading", rows = 3, testId = "brain-loading" }) {
  return (
    <div data-testid={testId} role="status" aria-live="polite" className="space-y-3 p-4">
      <span className="sr-only">{label}…</span>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full bg-slate-800" />
      ))}
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", detail, testId = "brain-empty" }) {
  return (
    <div data-testid={testId} className="p-6 text-center">
      <p className="text-sm font-medium text-slate-300">{title}</p>
      {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
    </div>
  );
}

export function ErrorState({ detail, onRetry, testId = "brain-error" }) {
  return (
    <div data-testid={testId} role="alert" className="p-6 text-center">
      <p className="text-sm font-medium text-rose-300">Couldn’t load this panel</p>
      {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
      {onRetry ? (
        <button
          onClick={onRetry}
          data-testid="brain-error-retry"
          className="mt-3 px-3 py-1.5 text-xs rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

// Backend capability flag is OFF (endpoint returned 404). Informational, not an error.
export function DisabledState({ capability = "This capability", flag, testId = "brain-disabled" }) {
  return (
    <div data-testid={testId} className="p-6 text-center">
      <p className="text-sm font-medium text-slate-300">{capability} is not enabled</p>
      <p className="mt-1 text-xs text-slate-500">
        Turn on the backend flag{flag ? ` (${flag})` : ""} to surface this data. Nothing is fabricated in its place.
      </p>
    </div>
  );
}

// Convenience: pick the right state for a useBrainResource() result.
export function ResourceGate({ resource, capability, flag, empty, children }) {
  if (!resource || resource.status === "loading") return <LoadingState />;
  if (resource.status === "disabled") return <DisabledState capability={capability} flag={flag} />;
  if (resource.status === "error")
    return <ErrorState detail={resource.error && resource.error.message} onRetry={resource.reload} />;
  if (resource.status === "empty") return empty || <EmptyState />;
  return children(resource.data);
}
